import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from "./api";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Box, debounce, Divider, Grid, Icon, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Paper, Stack, Typography, useTheme, Fade, CircularProgress } from "@mui/material";
import { useGetHotSpotsQuery } from "./rtk/image";
import { Tooltip } from "@mui/material";
import { HighlightAlt, RestartAlt, ThreeDRotation, ArrowBackIos, ArrowForwardIos, ArrowBack, ArrowForward, ChevronRight, ChevronLeft } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { hasPermission } from "./rtk/selectors";
import Part3DViewer from "./Part3DViewer/Part3DViewer";



const ImageViewer = (props) => {

    const { filename, minHeight, minWidth, bom, fullScreen, navigateNext, navigatePrevious, showNavigationArrow, onImageLoad } = props
    console.log("ImageViewer props", filename, bom)
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [hotspots, setHotspots] = useState([])
    const [zoomspots, setZoomspots] = useState([])
    const [selectedPartId, setSelectedPartId] = useState(null);
    const [hotspotContent, setHotspotContent] = useState(localStorage.getItem("hotspotContent") || null);
    const [hotspotContentMenu, setHotspotContentMenu] = useState(null);

    const [mouseRecentlyMoved, setMouseRecentlyMoved] = useState(true);
    const [hoveringWidget, setHoveringWidget] = useState(false);
    const timerRef = useRef(null);

    const [partViewerOpen, setPartViewerOpen] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    const { data: system, isLoading: partsLoading, isError: partsError } = useGetHotSpotsQuery({ bom: bom }, { skip: !bom });

    const [image, setImage] = useState(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(100);

    const imgRef = useRef(null);
    const wrapperRef = useRef(null);
    const transformComponentRef = useRef(null);
    const listRef = useRef(null);

    const user = useSelector(state => state.user.user);
    const userHasPermission = useSelector(state => hasPermission(state, "can_view_prices"), [user]);

    const parts = useMemo(() => {
        if (system && system.parts) {
            return system.parts.map((el) => {
                return {
                    id: el.id,
                    ref_number: el.ref_number,
                    english_description: el.english_description,
                    french_description: el.french_description,
                    part_number: el.part_number,
                    part_usd_retail_price: userHasPermission ? el.part_usd_retail_price : null,
                    part_file_3d: el.part_file_3d,
                }
            })
        }
        return []
    }, [system])

    useEffect(() => {
        console.log("ImageViewer", width)
        if (system) {
            createHotSpots();
        }
    }, [system, imgRef.current, selectedPartId, bom, height, width, imageSize, hotspotContent]);

    useEffect(() => {
        // save what the user wants to see in the hotspot in local storage
        if (hotspotContent) {
            localStorage.setItem("hotspotContent", hotspotContent);
        } else {
            localStorage.removeItem("hotspotContent");
        }
        // load what the user wants to see in the hotspot from local storage
        const savedHotspotContent = localStorage.getItem("hotspotContent");
        if (savedHotspotContent) {
            setHotspotContent(savedHotspotContent);
        } else {
            setHotspotContent(null);
        }
    }, [hotspotContent]);

    useEffect(() => {
        if (system) {
            createZoomSpots();
        }
    }, [system, imgRef.current, bom, height, width, imageSize]);

    useEffect(() => {
        if (filename) {
            setImageLoading(true);
            downloadImageData();
        }
    }, [filename]);

    // resizing the list when window is resized
    useEffect(() => {
        const handleResize = () => {
            if (listRef.current) {
                // getImageDimensions()
                setHeight(getListHeight())
                setWidth(getWidth())
            }
        };

        const rezizeObserver = new ResizeObserver(() => {
            if (wrapperRef.current) {
                // getImageDimensions()
                setHeight(getListHeight())
                setWidth(getWidth())

            }
        });
        if (wrapperRef.current) {
            rezizeObserver.observe(wrapperRef.current);
        }

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [wrapperRef.current, transformComponentRef.current, system])


    useEffect(() => {
        // Clear existing timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Set new timer if conditions are met
        if (mouseRecentlyMoved && !hoveringWidget) {
            timerRef.current = setTimeout(() => {
                setMouseRecentlyMoved(false);
            }, 2000);
        }

        // Cleanup on unmount
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [mouseRecentlyMoved, hoveringWidget]);

    const hotspotContentOptions = useMemo(() => {
        let vals = [
            { value: null, label: t("Ref Number") },
            { value: "part_number", label: t("Part Number") },
            { value: "english_description", label: t("English Description") },
            { value: "french_description", label: t("French Description") }

        ]
        if (userHasPermission) {
            vals.push({ value: "part_usd_retail_price", label: t("Price USD") });
        }
        return vals;

    }, [t, user, userHasPermission]);


    const downloadImageData = async () => {
        try {
            setImageLoading(true);

            // Use your existing authenticated API endpoint
            const response = await api.rawGet(`/download-image/`, {
                params: { id: filename }, // params should be in a params object
                responseType: 'blob' // config options at the same level
            });

            // Create blob URL for the image
            const imageBlob = new Blob([response.data]);
            const imageUrl = URL.createObjectURL(imageBlob);
            const img = new Image();
            img.src = imageUrl;
            img.onload = () => {
                setImageSize({ width: img.width, height: img.height });
                setImage(imageUrl);
                setTimeout(() => {
                    setImageLoading(false);
                }, 100);
            }
            img.onerror = (e) => {
                console.error('Error loading image', e);
                URL.revokeObjectURL(imageUrl); // Clean up on error
                setImageLoading(false);
            }
        } catch (error) {
            console.error('Error loading image:', error);
            setImageLoading(false);
        }
    }

    // Clean up blob URLs when component unmounts or image changes
    useEffect(() => {
        return () => {
            if (image && image.startsWith('blob:')) {
                URL.revokeObjectURL(image);
            }
        };
    }, [image]);


    const containedSize = () => {
        if (!imgRef.current) {
            return { width: 0, height: 0 }
        }
        let ratio = imgRef.current?.naturalWidth / imgRef.current?.naturalHeight
        let width = imgRef.current?.height * ratio
        let height = imgRef.current?.height
        if (width > imgRef.current?.width) {
            width = imgRef.current?.width
            height = imgRef.current?.width / ratio
        }
        return { width, height }
    }

    const computeTop = (y, size, hsH) => {
        const baseW = 1200
        if (imgRef.current !== undefined) {
            // taille du champs modifie
            // actual image size (with object fit "contain")
            let { width, height } = containedSize()
            let offsetH = (height - (imgRef.current?.height || baseW)) / 2
            const s = height * (hsH / (imgRef.current?.naturalHeight || baseW))
            return height * (y / (imgRef.current?.naturalHeight || baseW)) - (size - s) / 2 - offsetH
        }
        return 0
    }

    const computeLeft = (x, size, w) => {
        const baseW = 1200
        if (imgRef.current) {
            let { width, height } = containedSize()
            let offsetW = (width - (imgRef.current?.width || baseW)) / 2
            const s = width * (w / (imgRef.current?.naturalWidth || baseW))
            return width * (x / (imgRef.current?.naturalWidth || baseW)) - (size - s) / 2 - offsetW
        }
        return 10
    }

    const getHotspotSize = (base) => {
        const baseW = 1200
        if (imgRef.current) {
            // taille du champs modifie
            return (imgRef.current?.width || baseW) * (base / 1000)
        }
        return 10
    }

    const getHotspotContent = useCallback((part) => {
        if (hotspotContent === "part_number") {
            return part.part_number;
        } else if (hotspotContent === "english_description") {
            return part.english_description;
        } else if (hotspotContent === "french_description") {
            return part.french_description;
        } else if (hotspotContent === "part_usd_retail_price") {
            return part.part_usd_retail_price ? `$${part.part_usd_retail_price.toFixed(2)}` : t("N/A");
        } else {
            return '';
        }
    }, [hotspotContent]);

    const createHotSpots = () => {
        const hss = getHotspotSize(30)
        const height = getHotspotSize(15)
        const defaultBackgroundColor = hotspotContent ? '#FFFFFFFF' : '#FF000000';
        const selectedBackgroundColor = hotspotContent ? theme.palette.primary.main + "88" : theme.palette.primary.main + "88";

        // compute font size from width of the box (so 9 char fit in the box)
        let fontSize = Math.min(10, hss / 6);

        // if descripton, make it smaller
        if (hotspotContent === "english_description" || hotspotContent === "french_description") {
            fontSize = Math.min(8, hss / 6);
        }

        if (hotspotContent === "part_usd_retail_price") {
            fontSize = Math.min(22, hss / 4);
        }

        if (system) {
            setHotspots(system?.parts.map((el, j) => {
                return el.hotspots.map((hs, i) => (
                    <React.Fragment key={i}>
                        {
                            hotspotContent &&
                            <div
                                onClick={(e) => handlePartClick(e, el.id, true)}
                                key={i}
                                className="hotspot"
                                style={{
                                    backgroundColor: defaultBackgroundColor,
                                    width: hss,
                                    height: height,
                                    minHeight: "fit-content",
                                    position: "absolute",
                                    top: computeTop(hs.y, height, hs.height),
                                    left: computeLeft(hs.x, hss, hs.width),
                                    borderRadius: "15%",
                                }}>

                            </div>
                        }
                        <Tooltip title={<Typography variant="h5">{el.english_description}</Typography>} placement="right">
                            <Box sx={{
                                position: "absolute",
                                top: computeTop(hs.y, height, hs.height),
                                left: computeLeft(hs.x, hss, hs.width),
                                width: "fit-content",
                                height: "fit-content",
                                width: hss,
                                minHeight: height,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                zIndex: selectedPartId == el.id ? 2001 : 1001,
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: selectedPartId == el.id ? selectedBackgroundColor : theme.palette.primary.main + "88",
                                },
                                backgroundColor: (selectedPartId == el.id ? selectedBackgroundColor : "00000000"),
                                borderRadius: "15%",
                            }}
                                onClick={(e) => handlePartClick(e, el.id, true)}

                            >
                                <Typography
                                    sx={{
                                        color: ("black"),
                                        fontSize: fontSize,
                                        textAlign: "center",
                                        userSelect: "none",
                                        textWrap: "wrap",

                                    }}
                                >
                                    {getHotspotContent(el)}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </React.Fragment>
                ))
            }
            ))
        }
        else {
            setHotspots([])
        }
    }

    const createZoomSpots = () => {
        const zss = getHotspotSize(300)
        if (system) {
            setZoomspots(system?.parts.map((el, j) => {
                return el.hotspots.map((hs, i) => (
                    <div
                        key={i}
                        value={el.ref_number}
                        id={"zoom-" + el.id}
                        style={{
                            // backgroundColor:"#FF000088",
                            width: zss,
                            height: zss,
                            position: "absolute",
                            top: computeTop(hs.y, zss, hs.height),
                            left: computeLeft(hs.x, zss, hs.width),
                            zIndex: 1
                        }} />
                ))
            }))
        }
        else {
            setZoomspots([])
        }
    }

    const zoomToImage = (partId) => {
        if (transformComponentRef.current) {
            const { zoomToElement } = transformComponentRef.current;
            zoomToElement("zoom-" + partId);
        }
    };

    const onPartClick = (id) => {
        setSelectedPartId(id);
        const el = document.getElementById("zoom-" + id);
    }

    const zoomToListElement = (id) => {
        if (listRef.current) {
            const el = document.getElementById("list-part-" + id);
            if (el) {
                const { top, left, height } = el.getBoundingClientRect();
                const { top: listTop, left: listLeft } = listRef.current.getBoundingClientRect();
                const currentScrollTop = listRef.current.scrollTop;
                const offsetTop = top - listTop + currentScrollTop;
                const offsetLeft = left - listLeft;
                const listHeight = listRef.current.clientHeight;
                const listWidth = listRef.current.clientWidth;
                const scrollTop = offsetTop - (listHeight / 2) + (el.clientHeight / 2);
                const scrollLeft = offsetLeft - (listWidth / 2) + (el.clientWidth / 2);
                listRef.current.scrollTo({
                    top: scrollTop,
                    left: scrollLeft,
                    behavior: "smooth"
                });
            }
        }
    }

    const handlePartClick = (e, id, from_hotspot = false) => {
        switch (e.detail) {
            case 1:
                onPartClick(id)
                if (from_hotspot) {
                    zoomToListElement(id)
                }
                break;
            case 2:
                onPartClick(id)
                zoomToImage(id)
                break;
            case 3:
                onPartClick(id)
                let part = parts.find(el => el.id === id);
                console.log("part", part)
                if (part && part.part_file_3d) {
                    setPartViewerOpen(parts.find(el => el.id === id).part_number);
                }
                zoomToImage(id);
                break;
        }
    }

    const getListHeight = () => {
        // if <lg, remaining space
        // else all the wrapper
        // get lg px limit
        let breakpoint = theme.breakpoints.values.sm;
        if (document.body.clientWidth > breakpoint && wrapperRef.current) {
            return wrapperRef.current.clientHeight
        } else if (wrapperRef.current) {
            return wrapperRef.current.clientHeight * 0.5
        }
        return 0
    }
    const getWidth = () => {
        let breakpoint = theme.breakpoints.values.sm;
        let mdBreakpoint = theme.breakpoints.values.md;
        if (!wrapperRef.current) {
            return 100
        }
        if (document.body.clientWidth < breakpoint) {
            return wrapperRef.current.clientWidth
        }
        else if (parts && parts.length > 0 && document.body.clientWidth > mdBreakpoint) {
            return wrapperRef.current.clientWidth * 0.66
        }
        else if (parts && parts.length > 0) { // <md, >sm
            return wrapperRef.current.clientWidth * 0.75
        }
        return wrapperRef.current.clientWidth
    }

    const resetScale = () => {
        if (transformComponentRef.current) {
            const { resetTransform } = transformComponentRef.current;
            resetTransform();
        }
    }

    const baseColWidth = userHasPermission ? 3 : 4;

    const widthRatio = useMemo(() => {
        const xsWindowWidth = theme.breakpoints.values.xs;
        const smWindowWidth = theme.breakpoints.values.sm;
        const mdWindowWidth = theme.breakpoints.values.md;
        if (parts && parts.length > 0) {
            if (window.clientWidth < xsWindowWidth) {
                return 1; // 90% for xs
            } else if (window.clientWidth < smWindowWidth) {
                return 0.75; // 75% for sm
            } else if (window.clientWidth < mdWindowWidth) {
                return 0.66; // 66% for md
            } else {
                return 0.66; // 50% for lg and above
            }
        } else {
            return 1; // 90% for xs
        }
    }, [window.clientWidth, parts]);


    return (

        <div
            style={{ width: fullScreen ? "100%" : "90vw", height: '100%', minHeight: minHeight, overflow: "hidden" }}
            ref={wrapperRef}
            onMouseMove={() => setMouseRecentlyMoved(true)}
        >
            <Grid container spacing={0}>
                <Grid item xs={12} sm={(parts && parts.length > 0) ? 9 : 12} md={(parts && parts.length > 0) ? 8 : 12} sx={{ height, position: "relative", overflow: "hidden" }}>
                    {/* Loading overlay - shows over existing image */}
                    {imageLoading && (
                        <Box sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            backdropFilter: "blur(2px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1999,
                        }}>
                            <CircularProgress size={40} />
                        </Box>
                    )}

                    {
                        partViewerOpen &&
                        <Part3DViewer
                            partNumber={partViewerOpen}
                            open={partViewerOpen !== null}
                            onClose={() => setPartViewerOpen(null)}
                            style={{ position: "absolute", zIndex: 2000, top: 0, left: 0, width: "100%", height: "100%" }}
                            pageWidth={wrapperRef?.current?.clientWidth * widthRatio || 1200}
                            pageHeight={wrapperRef?.current?.clientHeight || 600}
                        />
                    }

                    {/* Always show image if available, even while loading */}
                    {image && imageSize.width > 0 && imageSize.height > 0 && (
                        <div style={{ opacity: imageLoading ? 0.7 : 1, transition: 'opacity 0.3s ease-in-out' }}>
                            <TransformWrapper
                                initialScale={1}
                                minScale={0.6}
                                ref={transformComponentRef}
                                style={{ width: '100%', height: height, display: partViewerOpen ? "none" : "block" }}
                            >
                                <TransformComponent wrapperStyle={{ width: '100%', height: height }}>
                                    <img
                                        src={image}
                                        style={{
                                            objectFit: 'contain',
                                            transition: 'opacity 0.3s ease-in-out'
                                        }}
                                        ref={imgRef}
                                        height={height}
                                        width={width}
                                        alt="image"
                                    />
                                    {
                                        //tooltip
                                        hotspots.map(el => el)
                                    }
                                    {
                                        //zoom
                                        zoomspots.map(el => el)
                                    }
                                </TransformComponent>
                            </TransformWrapper>
                        </div>
                    )}

                    {/* Previous Image Arrow */}
                    {navigatePrevious && (
                        <Paper elevation={6} sx={{
                            position: "absolute",
                            left: 15,
                            top: "50%",
                            borderRadius: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1500,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(4px)",
                            transition: "all 0.3s ease",
                            opacity: (showNavigationArrow || mouseRecentlyMoved) ? 1 : 0,
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.9)",
                                transform: "translateY(-50%) scale(1.1)",
                                boxShadow: theme.shadows[12],
                                opacity: 1
                            }
                        }}
                            onMouseEnter={() => {
                                setMouseRecentlyMoved(true);
                                setHoveringWidget(true);
                            }}
                            onMouseLeave={() => {
                                setHoveringWidget(false);
                            }}
                        >
                            <Tooltip title={t("Previous Image")} placement="right">
                                <IconButton
                                    onClick={navigatePrevious}
                                    size="large"
                                    sx={{
                                        p: 1,
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "transparent"
                                        }
                                    }}
                                    disabled={imageLoading}
                                >
                                    <ChevronLeft fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    )}

                    {/* Next Image Arrow */}
                    {navigateNext && (
                        <Paper elevation={6} sx={{
                            position: "absolute",
                            right: 15,
                            top: "50%",
                            borderRadius: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1500,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(4px)",
                            transition: "all 0.3s ease",
                            opacity: (showNavigationArrow || mouseRecentlyMoved) ? 1 : 0,
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.9)",
                                transform: "translateY(-50%) scale(1.1)",
                                boxShadow: theme.shadows[12],
                                opacity: 1
                            }
                        }}
                            onMouseEnter={() => {
                                setMouseRecentlyMoved(true);
                                setHoveringWidget(true);
                            }}
                            onMouseLeave={() => {
                                setHoveringWidget(false);
                            }}
                        >
                            <Tooltip title={t("Next Image")} placement="left">
                                <IconButton
                                    onClick={navigateNext}
                                    size="large"
                                    sx={{
                                        p: 1,
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "transparent"
                                        }
                                    }}
                                    disabled={imageLoading}
                                >
                                    <ChevronRight fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    )}
                </Grid>

                {
                    parts && parts.length > 0 &&
                    <Grid item xs={12} sm={3} md={4} sx={{ height: height, overflow: "hidden" }}>
                        <Paper elevation={10} sx={{ width: "100%", height: "100%", backgroundColor: theme.palette.primary.main }}>
                            <Box display="flex" alignItems="center" sx={{ padding: "10px", color: "black", height: "45px" }}>
                                <Typography variant="h6" sx={{ padding: "10px", color: "black", height: "45px" }}>{bom}</Typography>
                                <Tooltip title={t("Change shown content")}>
                                    <IconButton onClick={(e) => {
                                        setHotspotContentMenu(e.currentTarget);
                                    }}>
                                        <HighlightAlt sx={{ color: "black" }} />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={hotspotContentMenu}
                                    open={Boolean(hotspotContentMenu)}
                                    onClose={() => { setHotspotContentMenu(null); }}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    sx={{ zIndex: 11000 }}
                                >
                                    {
                                        hotspotContentOptions.map((option) => (
                                            <MenuItem key={option.value} onClick={() => {
                                                setHotspotContent(option.value);
                                            }}
                                                selected={hotspotContent === option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))
                                    }
                                </Menu>
                            </Box>
                            <List sx={{ overflow: "auto", height: height - 50, backgroundColor: "background.paper" }} dense ref={listRef} >
                                {
                                    parts && parts.map((part) => {
                                        return <div key={part.id} id={"list-part-" + part.id}>
                                            <ListItem sx={selectedPartId == part.id ? { backgroundColor: theme.palette.primary.main + "FF", boxShadow: 3, color: "black" } : {}}

                                                onClick={(e) => handlePartClick(e, part.id)}
                                            >
                                                <ListItemText sx={{ pr: 0 }}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                        <Grid container spacing={0} alignItems="center" sx={{ width: "100%" }}>
                                                            <Grid item xs={baseColWidth} sm={12} md={baseColWidth} lg={baseColWidth}>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <Typography sx={{ userSelect: "none" }}>{part.ref_number}.</Typography>
                                                                    {
                                                                        part.part_file_3d &&
                                                                        <Tooltip title={t("View 3D model")}>
                                                                            <IconButton onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setPartViewerOpen(part.part_number);
                                                                            }}
                                                                                sx={{ p: 0 }}
                                                                                color={selectedPartId == part.id ? "secondary" : "primary"}
                                                                            >
                                                                                <ThreeDRotation fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    }
                                                                </Stack>
                                                            </Grid>
                                                            <Grid item xs={baseColWidth} sm={12} md={baseColWidth} lg={baseColWidth}>
                                                                <Typography
                                                                    sx={{ userSelect: "none" }}
                                                                >
                                                                    {part.english_description}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={baseColWidth} sm={12} md={baseColWidth} lg={baseColWidth} sx={{ textAlign: "right" }}>
                                                                <Typography sx={{ color: "#999999" }} variant="body2">{part.part_number}</Typography>
                                                            </Grid>
                                                            {
                                                                userHasPermission &&
                                                                <Grid item xs={baseColWidth} sm={12} md={baseColWidth} lg={baseColWidth} sx={{ textAlign: "right" }}>
                                                                    <Typography sx={{ color: "#999999" }} variant="body2">{part.part_usd_retail_price ? `$${part.part_usd_retail_price.toFixed(2)}` : t("N/A")}</Typography>
                                                                </Grid>
                                                            }
                                                        </Grid>
                                                    </Box>
                                                </ListItemText>
                                            </ListItem>
                                            <Divider />
                                        </div>

                                    })
                                }
                            </List>
                        </Paper>
                    </Grid>
                }
            </Grid>


        </div >
    )

}
export default ImageViewer